<template>
	<main class="dashboard standard-plans">
		<MainLayout :chartLoaded="true" @change-section="onSectionChange" />

		<div class="main-section underHeader">
			<div class="panel-shell">
				<header class="panel-head">
					<div class="panel-head__intro">
						<p class="panel-eyebrow">Panel analista</p>
						<h1 class="panel-title">Planes de cuentas estándar</h1>
						<p class="panel-sub">
							Plantillas por rubro. No están asociadas a ninguna empresa:
							editarlas nunca modifica datos de clientes.
						</p>
					</div>
					<div class="panel-actions">
						<button type="button" class="pill" @click="volverAlPanel">
							Volver al panel
						</button>
						<button type="button" class="pill" :disabled="comparingExcel"
							@click="$refs.excelInput.click()">
							{{ comparingExcel ? "Comparando..." : "Comparar con Excel" }}
						</button>
						<input ref="excelInput" type="file" accept=".xlsx" class="is-hidden"
							@change="onExcelSelected">
						<button type="button"
							:class="['pill', sharedAccounts.length ? 'pill--alert' : '']"
							@click="showShared = !showShared">
							Cuentas compartidas
							<span v-if="sharedAccounts.length" class="pill__badge">
								{{ sharedAccounts.length }}
							</span>
						</button>
					</div>
				</header>

				<!-- Saneamiento: cuentas de plantilla compartidas con empresas u otros
					rubros (invariante I11). Visible bajo demanda para auditar en prod. -->
				<section v-if="showShared" class="panel-card shared-card">
					<div class="shared-head">
						<div>
							<h2 class="shared-title">Cuentas compartidas</h2>
							<p class="shared-sub">
								Cuentas de plantilla vinculadas a empresas o repetidas entre
								rubros. "Hacer únicas" mueve la plantilla a una copia nueva;
								las empresas conservan la cuenta original con todos sus datos.
							</p>
						</div>
						<button v-if="sharedAccounts.length" type="button"
							class="pill pill--alert" :disabled="splitting"
							@click="openSplitConfirm">
							Hacer únicas
						</button>
					</div>
					<p v-if="!sharedAccounts.length" class="shared-empty">
						Sin conflictos: ninguna cuenta de plantilla está compartida.
					</p>
					<div v-else class="shared-scroll">
						<table class="shared-table">
							<thead>
								<tr>
									<th>Cuenta</th>
									<th>Nivel 2</th>
									<th>Rubros</th>
									<th>Empresas afectadas</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="conflict in sharedAccounts" :key="conflict.account_plan_id">
									<td class="shared-strong">{{ conflict.level_3 }}</td>
									<td>{{ conflict.level_2 }}</td>
									<td>{{ conflict.business_types.join(", ") }}</td>
									<td>
										<span v-if="!conflict.companies.length" class="shared-muted">
											Solo repetida entre rubros
										</span>
										<span v-for="company in conflict.companies" :key="company.id"
											class="shared-chip">
											{{ company.name }} ({{ company.rut }})
										</span>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>

				<!-- Diff contra el Excel de los analistas (dry-run; nada se aplica
					hasta "Aplicar seleccionados"). Decisión 9: eliminaciones
					desmarcadas por defecto. -->
				<section v-if="excelDiff" class="panel-card">
					<div class="shared-head">
						<div>
							<h2 class="shared-title">Diferencias con el Excel</h2>
							<p class="shared-sub">
								Cambios detectados contra las plantillas actuales. Altas y
								renombres vienen marcados; eliminar requiere check explícito.
							</p>
						</div>
						<div class="diff-actions">
							<button type="button" class="pill" @click="discardExcelDiff">
								Descartar
							</button>
							<button type="button" class="pill pill--alert"
								:disabled="applyingDiff || !selectedOperations.length"
								@click="openApplyDiffConfirm">
								Aplicar seleccionados ({{ selectedOperations.length }})
							</button>
						</div>
					</div>
					<p v-if="!diffGroups.length" class="shared-empty">
						Sin diferencias: las plantillas ya coinciden con el Excel.
					</p>
					<div v-for="group in diffGroups" :key="group.title" class="diff-group">
						<h3 class="diff-group__title">{{ group.title }}</h3>
						<label v-for="item in group.items" :key="item.key"
							:class="['diff-item', `diff-item--${item.type}`]">
							<input type="checkbox" v-model="selectedOps[item.key]">
							<span>{{ item.text }}</span>
						</label>
					</div>
					<p v-if="excelDiff.unmapped_labels.length" class="shared-muted">
						Bloques del Excel sin rubro mapeado (ignorados):
						{{ excelDiff.unmapped_labels.join(", ") }}
					</p>
				</section>

				<!-- Editor de la plantilla del rubro seleccionado -->
				<section class="panel-card">
					<div class="editor-head">
						<label class="editor-label" for="business-type-select">Rubro</label>
						<b-select id="business-type-select" v-model="selectedBusinessType"
							@input="loadTemplate">
							<option v-for="type in businessTypes" :key="type.id" :value="type.id">
								{{ type.name }}
							</option>
						</b-select>
						<span class="editor-count" v-if="account_plans.length">
							{{ account_plans.length }} cuentas
						</span>
					</div>

					<div class="px-4 mb-2 is-hidden-mobile">
						<div class="columns is-vcentered ap-colheads">
							<div class="column is-1 has-text-right">Nro.</div>
							<div class="column is-2">Nivel 1</div>
							<div class="column is-2">Nivel 2</div>
							<div class="column is-3">Nivel 3 / Cuenta</div>
							<div class="column is-2">Cuenta X</div>
							<div class="column is-1">Orden</div>
							<div class="column is-1 has-text-centered">Operaciones</div>
						</div>
					</div>

					<div v-for="(item, index) in account_plans" :key="item.id"
						:class="['ap-row', rowBackgrounds[index], { 'ap-row--editing': indexEditing == index }]">
						<!-- Mientras se edita, el formulario TOMA EL LUGAR de la
							fila (misma grilla) en vez de duplicarla debajo. -->
						<AccountPlanRow v-if="indexEditing != index" :item="item" :index="index"
							show-balance-concept
							@edit-account="initializeEditingPlanAccount(index)"
							@delete-account="openDeleteConfirm(item.id)" />

						<AccountPlanInlineForm v-else
							:level1-values="level_1_values"
							:level2-options="level_2_values_depending"
							:balance-concepts="balance_concepts" :level1.sync="level1Editing"
							:level2.sync="level2Editing" :level3.sync="level3Editing"
							:order.sync="orderEditing"
							:balance-concept.sync="balanceConceptEditing" submit-label="OK"
							submit-class="theme_btn_dark" level2-remount cancellable
							@level1-change="onLevel1Change" @cancel="indexEditing = -1"
							@submit="editPlanAccount(item.id)" />
					</div>

					<div class="ap-row ap-row--new">
						<AccountPlanInlineForm :level1-values="level_1_values"
							:level2-options="level_2_values_depending"
							:balance-concepts="balance_concepts"
							:level1.sync="new_level_1_value" :level2.sync="new_level_2_value"
							:level3.sync="newAccount" :order.sync="new_order"
							:balance-concept.sync="newBalanceConcept" submit-label="+"
							submit-class="theme_btn_blue" @level1-change="onLevel1Change"
							@submit="addAccount" />
					</div>
				</section>
			</div>
		</div>
	</main>
</template>

<script>
import { mapActions } from "vuex";
import AccountPlanInlineForm from "@/components/companyInfo/AccountPlanInlineForm";
import AccountPlanRow from "@/components/companyInfo/AccountPlanRow";
import MainLayout from "@/components/layout/MainLayout";
import ConfirmRestore from "@/components/modals/ConfirmRestore";
import {
	consoleHandlerError,
	extractErrorMessage,
	notificate,
} from "@/utils/message";

export default {
	name: "StandardAccountPlan",
	components: {
		AccountPlanInlineForm,
		AccountPlanRow,
		MainLayout,
	},
	data() {
		return {
			businessTypes: [],
			selectedBusinessType: null,
			account_plans: [],
			level_1_values: [],
			level_2_values: {},
			level_2_values_depending: [],
			new_level_1_value: "",
			new_level_2_value: "",
			newAccount: "",
			new_order: "",
			newBalanceConcept: "",
			balance_concepts: [],
			level1Editing: "",
			level2Editing: "",
			level3Editing: "",
			orderEditing: "",
			balanceConceptEditing: "",
			indexEditing: -1,
			sharedAccounts: [],
			showShared: false,
			splitting: false,
			excelDiff: null,
			selectedOps: {},
			comparingExcel: false,
			applyingDiff: false,
			modalDefault: {
				parent: this,
				width: "25%",
				hasModalCard: true,
				canCancel: ["escape", "outside"],
				customClass: "center-always-modal",
			},
		};
	},
	async mounted() {
		try {
			const [typesData, sharedData] = await Promise.all([
				this.fetchTemplateBusinessTypes(),
				this.fetchTemplateSharedAccounts(),
			]);
			this.businessTypes = typesData.business_types;
			this.sharedAccounts = sharedData.shared_accounts;
			if (this.businessTypes.length) {
				this.selectedBusinessType = this.businessTypes[0].id;
				await this.loadTemplate();
			}
		} catch (error) {
			consoleHandlerError({ error, silent: true });
			notificate(
				"No pudimos cargar las plantillas. Intenta nuevamente.",
				"is-danger is-light",
			);
		}
	},
	computed: {
		diffGroups() {
			return this.excelDiff ? this.collectDiffItems(this.excelDiff) : [];
		},
		selectedOperations() {
			const operations = [];
			this.diffGroups.forEach((group) => {
				group.items.forEach((item) => {
					if (this.selectedOps[item.key]) {
						operations.push(item.operation);
					}
				});
			});
			return operations;
		},
		rowBackgrounds() {
			// Franjas por grupos de level_2 consecutivos, en una sola pasada
			// (mismo criterio que el editor por empresa).
			let groupIndex = 0;
			return this.account_plans.map((plan, i) => {
				if (i > 0 && plan.level_2 !== this.account_plans[i - 1].level_2) {
					groupIndex++;
				}
				return groupIndex % 2 === 0 ? "bg-group-a" : "bg-group-b";
			});
		},
	},
	methods: {
		...mapActions("analystCompany", [
			"applyTemplateExcelDiff",
			"compareTemplateExcel",
			"createTemplateAccount",
			"deleteTemplateAccount",
			"editTemplateAccount",
			"fetchTemplateAccountPlan",
			"fetchTemplateBusinessTypes",
			"fetchTemplateSharedAccounts",
			"splitTemplateSharedAccounts",
		]),
		onExcelSelected(event) {
			const file = event.target.files && event.target.files[0];
			event.target.value = "";
			if (!file) return;
			this.comparingExcel = true;
			return this.compareTemplateExcel(file)
				.then((data) => {
					this.excelDiff = data;
					this.selectedOps = this.buildInitialSelections(data);
				})
				.catch((error) => {
					notificate(
						extractErrorMessage(error, "No se pudo comparar el archivo."),
						"is-danger is-light",
					);
					consoleHandlerError({ error, silent: true });
				})
				.finally(() => {
					this.comparingExcel = false;
				});
		},
		collectDiffItems(diff) {
			const groups = [];
			if (diff.new_level_2.length) {
				groups.push({
					title: "Catálogo de niveles",
					items: diff.new_level_2.map((entry) => ({
						key: `create_level_2:${entry.level_1}|${entry.level_2}`,
						text: `Crear nivel: ${entry.level_1} / ${entry.level_2}`,
						type: "add",
						checked: true,
						operation: entry.operation,
					})),
				});
			}
			diff.business_types.forEach((entry) => {
				const items = [];
				if (entry.is_new) {
					items.push({
						key: `create_business_type:${entry.business_type}`,
						text: `Crear el rubro ${entry.business_type}`,
						type: "add",
						checked: true,
						operation: entry.create_business_type_operation,
					});
				}
				entry.renames.forEach((rename) => items.push({
					key: `rename:${entry.business_type}:${rename.account_plan_id}`,
					text: `Renombrar: ${rename.from} → ${rename.to}`,
					type: "mod",
					checked: true,
					operation: rename.operation,
				}));
				entry.level2_changes.forEach((change) => items.push({
					key: `l2:${entry.business_type}:${change.account_plan_id}`,
					text: `Mover ${change.level_3}: ` +
						`${change.from.level_1}/${change.from.level_2} → ` +
						`${change.to.level_1}/${change.to.level_2}`,
					type: "mod",
					checked: true,
					operation: change.operation,
				}));
				entry.additions.forEach((addition) => items.push({
					key: `create:${entry.business_type}:${addition.level_3}`,
					text: `Agregar: ${addition.level_1} / ${addition.level_2} / ` +
						`${addition.level_3}`,
					type: "add",
					checked: true,
					operation: addition.operation,
				}));
				entry.deletions.forEach((deletion) => items.push({
					key: `delete:${entry.business_type}:${deletion.account_plan_id}`,
					text: `Eliminar: ${deletion.level_1} / ${deletion.level_2} / ` +
						`${deletion.level_3}`,
					type: "del",
					checked: false,
					operation: deletion.operation,
				}));
				if (items.length) {
					groups.push({
						title: entry.is_new
							? `${entry.business_type} (rubro nuevo)`
							: entry.business_type,
						items,
					});
				}
			});
			return groups;
		},
		buildInitialSelections(diff) {
			// Objeto completo upfront: reactivo en Vue 2 sin $set.
			const selections = {};
			this.collectDiffItems(diff).forEach((group) => {
				group.items.forEach((item) => {
					selections[item.key] = item.checked;
				});
			});
			return selections;
		},
		discardExcelDiff() {
			this.excelDiff = null;
			this.selectedOps = {};
		},
		openApplyDiffConfirm() {
			this.$buefy.modal.open({
				...this.modalDefault,
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: "confirmEvent",
					tittleRestore:
						`¿Aplicar ${this.selectedOperations.length} cambios del Excel?`,
					subTittleRestore:
						"Solo se modifican las plantillas estándar: las empresas " +
						"existentes conservan sus planes de cuentas.",
					msgOkAction: "Aplicar",
				},
				events: {
					confirmEvent: () => {
						this.applyExcelDiffSelected();
					},
				},
			});
		},
		async applyExcelDiffSelected() {
			this.applyingDiff = true;
			try {
				const result = await this.applyTemplateExcelDiff(
					this.selectedOperations,
				);
				notificate(
					`Se aplicaron ${result.total} cambios del Excel.`,
					"is-success is-light",
				);
				this.discardExcelDiff();
				// El apply puede crear rubros (SaaS): refrescar también el selector.
				const typesData = await this.fetchTemplateBusinessTypes();
				this.businessTypes = typesData.business_types;
				await Promise.all([this.loadTemplate(), this.loadSharedAccounts()]);
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudieron aplicar los cambios."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			} finally {
				this.applyingDiff = false;
			}
		},
		async loadTemplate() {
			if (!this.selectedBusinessType) return;
			try {
				const data = await this.fetchTemplateAccountPlan(
					this.selectedBusinessType,
				);
				this.account_plans = data.account_plans;
				this.level_1_values = data.level_1_values;
				this.level_2_values = data.level_2_values;
				this.balance_concepts = data.balance_concepts || [];
				this.indexEditing = -1;
			} catch (error) {
				consoleHandlerError({ error, silent: true });
				notificate(
					"No pudimos cargar la plantilla del rubro.",
					"is-danger is-light",
				);
			}
		},
		async loadSharedAccounts() {
			try {
				const data = await this.fetchTemplateSharedAccounts();
				this.sharedAccounts = data.shared_accounts;
			} catch (error) {
				consoleHandlerError({ error, silent: true });
			}
		},
		onLevel1Change(val) {
			this.level_2_values_depending = this.level_2_values[val]["items"];
		},
		initializeEditingPlanAccount(iPlan) {
			this.indexEditing = iPlan;
		},
		async addAccount() {
			if (!this.new_level_2_value || !this.newAccount) {
				notificate("Debe llenar los datos.");
				return;
			}
			try {
				await this.createTemplateAccount({
					businessTypeId: this.selectedBusinessType,
					level2Id: this.new_level_2_value,
					accountName: this.newAccount,
					order: this.new_order || 100,
					balanceConcept: this.newBalanceConcept,
				});
				notificate("Cuenta agregada a la plantilla.", "is-success is-light");
				this.new_level_1_value = "";
				this.new_level_2_value = "";
				this.newAccount = "";
				this.new_order = "";
				this.newBalanceConcept = "";
				await this.loadTemplate();
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudo crear la cuenta."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		async editPlanAccount(idPlanAccount) {
			if (!this.level2Editing || !this.level3Editing) {
				notificate("Debe llenar todos los datos.");
				return;
			}
			try {
				await this.editTemplateAccount({
					businessTypeId: this.selectedBusinessType,
					accountId: idPlanAccount,
					level2Id: this.level2Editing,
					accountName: this.level3Editing,
					order: this.orderEditing || 100,
					// "" viaja como null y limpia: el form refleja el estado final.
					balanceConcept: this.balanceConceptEditing,
				});
				notificate("Cuenta grabada exitosamente.", "is-success is-light");
				this.indexEditing = -1;
				await this.loadTemplate();
			} catch (error) {
				// 409 = compartida (sanear con "Cuentas compartidas") o duplicada.
				notificate(
					extractErrorMessage(error, "No se pudo editar la cuenta."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		async deletePlanAccount(idPlanAccount) {
			try {
				await this.deleteTemplateAccount({
					businessTypeId: this.selectedBusinessType,
					accountId: idPlanAccount,
				});
				notificate("Cuenta eliminada de la plantilla.", "is-success is-light");
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudo eliminar la cuenta."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			} finally {
				await this.loadTemplate();
			}
		},
		openDeleteConfirm(idPlanAccount) {
			this.$buefy.modal.open({
				...this.modalDefault,
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: "confirmEvent",
					tittleRestore: "¿Desea eliminar esta cuenta de la plantilla?",
					subTittleRestore:
						"Solo afecta la plantilla estándar: las empresas existentes " +
						"conservan sus planes de cuentas.",
					msgOkAction: "Eliminar",
				},
				events: {
					confirmEvent: () => {
						this.deletePlanAccount(idPlanAccount);
					},
				},
			});
		},
		openSplitConfirm() {
			this.$buefy.modal.open({
				...this.modalDefault,
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: "confirmEvent",
					tittleRestore: "¿Hacer únicas las cuentas compartidas?",
					subTittleRestore:
						"La plantilla pasa a usar copias nuevas de estas cuentas. Las " +
						"empresas conservan las cuentas originales con sus " +
						"clasificaciones y devengos intactos.",
					msgOkAction: "Hacer únicas",
				},
				events: {
					confirmEvent: () => {
						this.splitSharedAccounts();
					},
				},
			});
		},
		async splitSharedAccounts() {
			this.splitting = true;
			try {
				const result = await this.splitTemplateSharedAccounts();
				notificate(
					`Se sanearon ${result.fixed} cuentas compartidas.`,
					"is-success is-light",
				);
				await Promise.all([this.loadSharedAccounts(), this.loadTemplate()]);
			} catch (error) {
				notificate(
					extractErrorMessage(error, "No se pudo sanear las cuentas."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			} finally {
				this.splitting = false;
			}
		},
		volverAlPanel() {
			this.$router.push({ name: "panel-analista" });
		},
		onSectionChange(section, tab) {
			// Igual que PanelAnalista: el rail del sidebar navega al Dashboard.
			const query = { section };
			if (tab) query.tab = tab;
			this.$router.push({ path: "/dashboard", query }).catch((err) => {
				if (err.name !== "NavigationDuplicated") throw err;
			});
		},
	},
	watch: {
		indexEditing(newVal) {
			// newVal puede ser 0 (primera fila): comparar contra -1, no truthiness.
			if (newVal !== null && newVal >= 0) {
				this.level1Editing = this.account_plans[newVal].level_1;
				this.level3Editing = this.account_plans[newVal].level_3;
				this.orderEditing = this.account_plans[newVal].order;
				this.balanceConceptEditing =
					this.account_plans[newVal].balance_concept || "";
				this.onLevel1Change(this.level1Editing);
				this.level2Editing = this.level_2_values_depending.find(
					(item) => item.level_2 == this.account_plans[newVal].level_2,
				).id;
			}
		},
	},
};
</script>
<style scoped lang="scss">
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss).
.standard-plans {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
}

.panel-shell {
	max-width: 1200px;
	margin: 0 auto;
	padding: 1.5rem 1rem 3rem;
}

// ── Encabezado ──────────────────────────────────────────────────────────
.panel-head {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1.25rem;
}

.panel-eyebrow {
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	color: var(--ap-teal-deep);
}

.panel-title {
	font-size: 1.6rem;
	font-weight: 700;
	letter-spacing: -0.01em;
	color: var(--ap-ink);
}

.panel-sub {
	color: var(--ap-slate);
	max-width: 46ch;
}

.panel-actions {
	display: flex;
	gap: 0.5rem;
}

.pill {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	padding: 0.45rem 1rem;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-pill);
	background: var(--ap-paper);
	color: var(--ap-ink-2);
	font-family: var(--ap-font);
	font-weight: 600;
	cursor: pointer;
	transition: border-color 0.18s var(--ap-ease), color 0.18s var(--ap-ease);

	&:hover {
		border-color: var(--ap-teal);
		color: var(--ap-teal-deep);
	}

	&:disabled {
		opacity: 0.6;
		cursor: wait;
	}

	&--alert {
		border-color: var(--ap-alert);
		color: var(--ap-alert);

		&:hover {
			border-color: var(--ap-alert);
			color: var(--ap-alert);
		}
	}
}

.pill__badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 1.4em;
	padding: 0 0.35em;
	border-radius: var(--ap-radius-pill);
	background: var(--ap-alert);
	color: var(--ap-paper);
	font-size: 0.8em;
	font-weight: 700;
}

// ── Cards ───────────────────────────────────────────────────────────────
.panel-card {
	background: var(--ap-paper);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius);
	box-shadow: var(--ap-shadow-card);
	padding: 1.25rem;
	margin-bottom: 1.25rem;
}

// ── Cuentas compartidas ─────────────────────────────────────────────────
.shared-head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 0.75rem;
}

.shared-title {
	font-size: 1.1rem;
	font-weight: 700;
	color: var(--ap-ink);
}

.shared-sub {
	color: var(--ap-slate);
	font-size: 0.9rem;
	max-width: 62ch;
}

.shared-empty {
	color: var(--ap-ok, var(--ap-teal-deep));
	font-weight: 600;
}

.shared-scroll {
	overflow-x: auto;
}

.shared-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.9rem;

	th {
		text-align: left;
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: var(--ap-muted);
		padding: 0.5rem 0.75rem;
		border-bottom: 1px solid var(--ap-line-strong);
	}

	td {
		padding: 0.5rem 0.75rem;
		border-bottom: 1px solid var(--ap-line);
		vertical-align: top;
	}
}

.shared-strong {
	font-weight: 600;
	color: var(--ap-ink);
}

.shared-muted {
	color: var(--ap-muted);
	font-style: italic;
}

.shared-chip {
	display: inline-block;
	margin: 0 0.25rem 0.25rem 0;
	padding: 0.15rem 0.6rem;
	border-radius: var(--ap-radius-pill);
	background: var(--ap-mist);
	border: 1px solid var(--ap-line);
	color: var(--ap-ink-2);
	font-size: 0.85em;
}

// ── Diff con el Excel ───────────────────────────────────────────────────
.diff-actions {
	display: flex;
	gap: 0.5rem;
	align-items: flex-start;
}

.diff-group {
	margin-bottom: 1rem;
}

.diff-group__title {
	font-weight: 700;
	color: var(--ap-ink);
	margin-bottom: 0.35rem;
}

.diff-item {
	display: flex;
	align-items: baseline;
	gap: 0.5rem;
	padding: 0.2rem 0.5rem;
	border-radius: var(--ap-radius-sm);
	cursor: pointer;
	font-size: 0.92rem;

	&:hover {
		background: var(--ap-mist);
	}

	&--add span {
		color: var(--ap-teal-deep);
	}

	&--del span {
		color: var(--ap-alert);
	}

	&--mod span {
		color: var(--ap-slate);
	}
}

// ── Editor ──────────────────────────────────────────────────────────────
.editor-head {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1rem;
}

.editor-label {
	font-weight: 600;
	color: var(--ap-slate);
}

.editor-count {
	color: var(--ap-muted);
	font-size: 0.9rem;
}

.ap-colheads .column {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--ap-muted);
}

.ap-row {
	padding: 0.5rem 0.75rem;
	margin-bottom: 0.25rem;
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
	transition: border-color 0.2s var(--ap-ease);

	&:hover {
		border-color: var(--ap-line-strong);
	}

	&--new {
		background-color: var(--ap-mist);
		border-style: dashed;
	}

	// Fila en edición: el acento marca DÓNDE se está editando sin gritar.
	&--editing {
		border-color: var(--ap-teal);
		box-shadow: 0 0 0 3px var(--ap-teal-wash);
	}
}

.bg-group-a {
	background-color: var(--ap-teal-wash);
}

.bg-group-b {
	background-color: var(--ap-paper);
}
</style>
